import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CropImageEdit,
  TemplateStudioCropShape,
} from "@/components/scheduled-briefings/modals/CropImageModal";
import ScheduledBriefingsModals from "@/components/scheduled-briefings/modals/ScheduledBriefingsModals";
import { ContinuousA4Preview } from "@/components/scheduled-briefings/template-studio/ContinuousA4Preview";
import { TemplateStudioChatHeader } from "@/components/scheduled-briefings/template-studio/TemplateStudioChatHeader";
import { TemplateStudioWorkflowHeader } from "@/components/scheduled-briefings/template-studio/TemplateStudioWorkflowHeader";
import {
  AttachmentBubbleImage,
  AttachmentBubbleRow,
} from "@/components/ui/chat/AttachmentBubbles";
import ChatInput from "@/components/ui/chat/ChatInput";
import { ChatInputRequestDock } from "@/components/ui/chat/ChatInputRequestDock";
import UserBubble from "@/components/ui/chat/UserBubble";
import { ReportImageCropContext } from "@/components/ui/report/ReportBannerImage/ReportBannerImageSpec";
import { reportComponentThemeSchema } from "@/components/ui/report/reportPresentation";
import { TemplateStudioCanvasSkeleton } from "@/components/ui/report/TemplateStudioCanvasSkeleton/TemplateStudioCanvasSkeleton";
import {
  A4_PREVIEW_WIDTH_PX,
  TemplateStudioEmptyCanvas,
} from "@/components/ui/report/TemplateStudioEmptyCanvas/TemplateStudioEmptyCanvas";
import { standardAuthFetch } from "@/data-access/core/authenticatedFetch";
import useFeature from "@/lib/feature-management/useFeature";
import { validateTemplateLayout } from "@/lib/template-layout/validateTemplateLayout";
import {
  findNewBannerSrcs,
  loadImageSize,
  resolveBannerSizeTip,
} from "@/lib/template-studio/bannerSizeTip";
import {
  type LayoutImage,
  listLayoutImages,
  parseChatImageEditRequest,
  resolveChatImageEditTarget,
  runChatImageEdit,
} from "@/lib/template-studio/chatImageEdit";
import {
  buildTemplateStudioDraftFromCompiledSpec,
  createDefaultTemplateStudioTheme,
  getDisplaySpecFromDraft,
  getRefinementPreviousFromDraft,
  resolveReportThemeFromDraft,
  type TemplateStudioCompiledSpec,
  type TemplateStudioDraft,
} from "@/lib/template-studio/draft/templateStudioDraft";
import { resolveLayoutAssetPreviewUrl } from "@/lib/template-studio/layoutAssetPreviewUrl";
import {
  isSameLayoutImage,
  replaceLayoutImage,
} from "@/lib/template-studio/layoutImageAssets";
import {
  ingestGraphQLTemplateLayoutAssetFromUrl,
  type TemplateLayoutAsset,
  uploadGraphQLTemplateLayoutAsset,
} from "@/lib/template-studio/repository/graphqlTemplateLayoutAssetRepository";
import { resolveSaveTemplateModalSummary } from "@/lib/template-studio/save/saveTemplateModalSummary";
import { useTemplateStudioSave } from "@/lib/template-studio/save/useTemplateStudioSave";
import { templateStudioGenerateLayoutUrl } from "@/lib/template-studio/templateStudioApi";
import type {
  TemplateStudioConversation,
  TemplateStudioConversationScope,
} from "@/lib/template-studio/templateStudioConversations";
import { duplicateTemplateDefaultName } from "@/lib/template-studio/templateStudioEntry";
import {
  buildGenerateLayoutPrompt,
  buildTemplateStudioPreviewContext,
  findPastedImageUrls,
  replacePastedImageUrls,
  resolvePromptBrandColor,
  resolvePromptGoogleFont,
  resolvePromptImagePlacement,
  TEMPLATE_STUDIO_CONTENT_AVAILABLE_COMPONENTS,
  TEMPLATE_STUDIO_REPORT_AVAILABLE_COMPONENTS,
  type TemplateStudioImagePlacement,
  type TemplateStudioTurn,
} from "@/lib/template-studio/templateStudioPromptHelpers";
import { useStudioConversation } from "@/lib/template-studio/useStudioConversation";
import { useScheduledBriefingsFullscreen } from "@/routes/scheduled-briefings/useScheduledBriefingsFullscreen";
import type { TemplateLayout } from "@/types/template-layout/templateLayout.types";

interface GenerateLayoutApiResponse {
  ok: boolean;
  kind?: "spec" | "reply";
  layoutChanged?: boolean;
  compiledSpec?: TemplateStudioCompiledSpec;
  componentTheme?: unknown;
  summary?: string;
  reply?: string;
  unsupportedRequests?: string[];
  themeUpdates?: { fontFamily?: string; primaryColor?: string };
  errors?: { code: string; message: string; path?: string }[];
}

const isTemplateStudioCompiledSpec = (
  value: unknown,
): value is TemplateStudioCompiledSpec => {
  if (typeof value !== "object" || value === null) return false;

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.catalogVersion === "string" &&
    candidate.catalogVersion.trim().length > 0 &&
    typeof candidate.root === "string" &&
    typeof candidate.elements === "object" &&
    candidate.elements !== null
  );
};

const parseGenerateLayoutHttpError = async (
  response: Response,
): Promise<string> => {
  try {
    const body = (await response.json()) as Record<string, unknown> | null;
    const detail = body?.detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
    const error = body?.error;
    if (typeof error === "string" && error.length > 0) return error;

    const errors = body?.errors;
    if (Array.isArray(errors)) {
      const messages = errors
        .map((entry) =>
          typeof entry === "object" &&
          entry !== null &&
          "message" in entry &&
          typeof entry.message === "string"
            ? entry.message
            : null,
        )
        .filter((message): message is string => message !== null);
      if (messages.length > 0) return messages.join("; ");
    }
  } catch {
    // Non-JSON error bodies fall through to the status fallback.
  }

  return `Layout generation failed (${response.status})`;
};

type AppliedGenerateLayoutResponse =
  | {
      ok: true;
      draft: TemplateStudioDraft | null;
      summary: string;
      reply?: string;
    }
  | { ok: false; error: string };

/**
 * The draft and chat outcome one generate-layout response produces. Live
 * turns and the replay of a stored conversation both use it, so a resumed
 * conversation lands on the same layout.
 */
const applyGenerateLayoutResponse = async ({
  data,
  draft,
  text,
  imageUrls,
  imagePlacement,
  flexibleTemplateStudio,
  isFontAvailable,
}: {
  data: GenerateLayoutApiResponse;
  draft: TemplateStudioDraft | null;
  /** The message as typed; font and colour hints are read from it. */
  text: string;
  /** Stored URLs of images attached to the message. */
  imageUrls: readonly string[];
  /** Where the user said the attached image goes, if they did. */
  imagePlacement: TemplateStudioImagePlacement | null;
  flexibleTemplateStudio: boolean;
  isFontAvailable: (stylesheetUrl: string) => Promise<boolean>;
}): Promise<AppliedGenerateLayoutResponse> => {
  const promptGoogleFont = resolvePromptGoogleFont(text);
  const promptBrandColor = resolvePromptBrandColor(text);

  if (
    flexibleTemplateStudio &&
    data.ok &&
    data.kind === "reply" &&
    data.layoutChanged === false &&
    typeof data.reply === "string" &&
    data.reply.trim()
  ) {
    return {
      ok: true,
      draft,
      reply: data.reply,
      summary: data.summary ?? "No layout changes were made.",
    };
  }

  if (
    !data.ok ||
    !(
      isTemplateStudioCompiledSpec(data.compiledSpec) ||
      (data.compiledSpec === undefined &&
        ((data.unsupportedRequests?.length ?? 0) > 0 || draft != null))
    ) ||
    typeof data.summary !== "string" ||
    data.summary.trim().length === 0
  ) {
    return {
      ok: false,
      error:
        data.errors?.map((e) => e.message).join("; ") ??
        "Layout generation failed.",
    };
  }

  const componentThemeResult =
    flexibleTemplateStudio && data.componentTheme !== undefined
      ? reportComponentThemeSchema.safeParse(data.componentTheme)
      : undefined;
  if (componentThemeResult && !componentThemeResult.success) {
    return {
      ok: false,
      error:
        "Herald returned unsupported report styles. Refresh the page and try again.",
    };
  }
  const componentTheme = componentThemeResult?.data;
  let summary = data.summary.trim();
  let nextDraftResult = draft;
  const compiledSpec = isTemplateStudioCompiledSpec(data.compiledSpec)
    ? data.compiledSpec
    : draft != null
      ? getRefinementPreviousFromDraft(draft)
      : undefined;
  if (compiledSpec != null) {
    const fontFamily = data.themeUpdates?.fontFamily;
    let fontLoadWarning = "";
    let googleFont =
      fontFamily && fontFamily !== promptGoogleFont?.family
        ? {
            family: fontFamily,
            stylesheetUrl: `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;600;700&display=swap`,
          }
        : promptGoogleFont;
    if (
      googleFont &&
      googleFont.stylesheetUrl !== draft?.theme.fontStylesheetUrl &&
      !(await isFontAvailable(googleFont.stylesheetUrl))
    ) {
      fontLoadWarning = `Not applied: Could not load Google Font ${googleFont.family}. The existing font was kept.`;
      googleFont = null;
    }
    const nextDraft = buildTemplateStudioDraftFromCompiledSpec({
      compiledSpec,
      priorDraft: draft,
      themeHints: {
        googleFont,
        brandColor: data.themeUpdates?.primaryColor ?? promptBrandColor,
        componentTheme,
      },
      bannerImageUrls: imagePlacement === "top" ? imageUrls : [],
      bodyImageUrls: imagePlacement === "inside" ? imageUrls : [],
    });
    const priorTheme = draft?.theme ?? createDefaultTemplateStudioTheme();
    const themeChanges: string[] = [];
    if (nextDraft.theme.fontFamily !== priorTheme.fontFamily) {
      themeChanges.push(
        `Changed template font to ${nextDraft.theme.fontFamily}.`,
      );
    }
    if (nextDraft.theme.primaryColor !== priorTheme.primaryColor) {
      themeChanges.push(
        `Changed template brand colour to ${nextDraft.theme.primaryColor}.`,
      );
    }
    const keepsLayout =
      data.layoutChanged === false || data.compiledSpec === undefined;
    if (
      keepsLayout &&
      (JSON.stringify(priorTheme.reportSection) !==
        JSON.stringify(nextDraft.theme.reportSection) ||
        JSON.stringify(priorTheme.reportHighlight) !==
          JSON.stringify(nextDraft.theme.reportHighlight))
    ) {
      themeChanges.push("Updated report theme.");
    }
    if (themeChanges.length > 0 && keepsLayout) {
      summary = data.unsupportedRequests?.length
        ? `Not applied: ${data.unsupportedRequests.join("; ")}`
        : "";
    }
    summary = [summary, fontLoadWarning, ...themeChanges]
      .filter(Boolean)
      .join(" ");
    nextDraftResult =
      keepsLayout && draft != null
        ? { ...draft, theme: nextDraft.theme }
        : nextDraft;
  }
  return {
    ok: true,
    draft: nextDraftResult,
    summary,
    reply:
      flexibleTemplateStudio && typeof data.reply === "string"
        ? data.reply
        : undefined,
  };
};

/** The draft's report component styles, which Herald keeps unless asked to change them. */
const resolveDraftComponentTheme = (draft: TemplateStudioDraft | null) =>
  draft && (draft.theme.reportSection || draft.theme.reportHighlight)
    ? {
        reportSection: draft.theme.reportSection,
        reportHighlight: draft.theme.reportHighlight,
      }
    : undefined;

/** Whether a Google Fonts stylesheet loads within 5 s. Throws if `signal` aborts. */
const isGoogleFontAvailable = async (
  stylesheetUrl: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  try {
    const stylesheet = await fetch(stylesheetUrl, {
      signal: AbortSignal.any([
        ...(signal ? [signal] : []),
        AbortSignal.timeout(5_000),
      ]),
    });
    return stylesheet.ok;
  } catch (fontError) {
    if (signal?.aborted) throw fontError;
    return false;
  }
};

/** An edit the crop dialog made, and the original image it was made from. */
interface CropDialogEdit extends CropImageEdit {
  src: string;
}

const readStoredDraft = (value: unknown): TemplateStudioDraft | null => {
  const result = validateTemplateLayout(value);
  return result.success ? result.data : null;
};

/**
 * Rebuilds the chat and canvas by replaying a stored conversation. Fonts are
 * checked again, so one that doesn't load is never applied.
 */
/** How the "Which image?" question names a layout image. */
const describeLayoutImage = (image: LayoutImage, index: number) =>
  image.banner
    ? "The banner"
    : image.alt.trim()
      ? `Image ${index + 1}: ${image.alt.trim()}`
      : `Image ${index + 1}`;

/** Drops a turn's banner size tips for `src`: they describe the image before it was cropped. */
const withoutSizeTipsFor = (
  turn: TemplateStudioTurn,
  src: string,
): TemplateStudioTurn => ({
  ...turn,
  notes: turn.notes?.filter(
    (note) =>
      note.cropImageSrc == null || !isSameLayoutImage(note.cropImageSrc, src),
  ),
});

const restoreStudioConversation = async (
  conversation: TemplateStudioConversation,
): Promise<{
  draft: TemplateStudioDraft | null;
  turns: TemplateStudioTurn[];
}> => {
  let draft = readStoredDraft(conversation.seed.initialDraft);
  const turns: TemplateStudioTurn[] = [];
  const fontChecks = new Map<string, Promise<boolean>>();
  const checkFont = (stylesheetUrl: string) => {
    const check =
      fontChecks.get(stylesheetUrl) ?? isGoogleFontAvailable(stylesheetUrl);
    fontChecks.set(stylesheetUrl, check);
    return check;
  };
  for (const turn of conversation.turns) {
    if (turn.kind === "reset") {
      draft = readStoredDraft(turn.seed?.initialDraft);
      turns.push({
        id: turn.requestId,
        text: turn.text,
        summary: String(turn.response.summary ?? ""),
      });
      continue;
    }
    const applied = await applyGenerateLayoutResponse({
      data: turn.response as GenerateLayoutApiResponse,
      draft,
      text: turn.text,
      imageUrls: turn.images?.map((image) => image.url) ?? [],
      imagePlacement: turn.imagePlacement ?? null,
      flexibleTemplateStudio: true,
      isFontAvailable: checkFont,
    });
    if (applied.ok) draft = applied.draft;
    turns.push({
      id: turn.requestId,
      text: turn.text,
      ...(turn.images?.length ? { images: turn.images } : {}),
      summary: applied.ok ? applied.summary : applied.error,
      reply: applied.ok ? applied.reply : undefined,
    });
  }
  if (conversation.pending != null) {
    turns.push({
      id: conversation.pending.requestId,
      text: conversation.pending.text,
      summary:
        "Herald is still working on this, or its result wasn't saved. Refresh to check again.",
    });
  }
  return { draft, turns };
};

const TEMPLATE_STUDIO_EXAMPLE_PROMPTS = [
  "Create a media coverage briefing with 4 KPIs, a coverage-over-time chart, and a top articles table.",
  "Build a daily briefing with an editor's note, a contents list, and three article sections.",
  "I need an executive visibility briefing with metrics, a mentions chart and a pull quote.",
];

const TEMPLATE_STUDIO_INITIAL_GENERATION_STAGES = [
  "Understanding the request",
  "Generating structure",
  "Designing the details",
  "Finalizing layout",
] as const;

const TEMPLATE_STUDIO_GENERATION_STAGE_INTERVAL_MS = 620;

const TemplateStudioComponentChips = ({
  components,
  onAddComponent,
}: {
  components: string[];
  onAddComponent: (component: string) => void;
}) => (
  <div className="flex flex-wrap gap-1.5">
    {components.map((component) => (
      <button
        key={component}
        type="button"
        onClick={() => onAddComponent(component)}
        className="cursor-pointer rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600 transition-colors hover:bg-violet-50 hover:text-violet-700"
      >
        {component}
      </button>
    ))}
  </div>
);

const TemplateStudioEmptyState = ({
  onSelectPrompt,
  onAddComponent,
  showExamplePrompts,
}: {
  onSelectPrompt: (prompt: string) => void;
  onAddComponent: (component: string) => void;
  /** Hidden once a saved template is loaded; they'd replace it, not refine it. */
  showExamplePrompts: boolean;
}) => (
  <div className="mt-5 flex flex-1 flex-col gap-6">
    {showExamplePrompts ? (
      <div className="flex flex-col gap-1.5">
        {TEMPLATE_STUDIO_EXAMPLE_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onSelectPrompt(prompt)}
            className="block w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-[13px] text-gray-600 transition-colors hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
          >
            {prompt}
          </button>
        ))}
      </div>
    ) : null}
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Report layout
        </p>
        <TemplateStudioComponentChips
          components={TEMPLATE_STUDIO_REPORT_AVAILABLE_COMPONENTS}
          onAddComponent={onAddComponent}
        />
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Content
        </p>
        <TemplateStudioComponentChips
          components={TEMPLATE_STUDIO_CONTENT_AVAILABLE_COMPONENTS}
          onAddComponent={onAddComponent}
        />
      </div>
    </div>
  </div>
);

const TEMPLATE_STUDIO_CHAT_FOOTER_NOTE = {
  create:
    "Templates built here become available to every customer. Layout is locked once saved.",
  edit: "Templates built here become available to every customer. Layout locks once a briefing uses this template.",
} as const;

/**
 * `edit` saves back to `template`; `duplicate` starts from its document and
 * creates a new template on Save. Remount (via `key`) to switch templates.
 */
type TemplateStudioProps =
  | { mode?: "create" }
  | {
      mode: "edit" | "duplicate";
      template: TemplateLayout;
    };

const resolveTemplateStudioIntro = (
  mode: TemplateStudioProps["mode"],
  seedTemplate: TemplateLayout | undefined,
): string => {
  if (seedTemplate == null) {
    return "Describe the briefing or report you want. I’ll build the layout and you can refine it in conversation.";
  }

  const subject =
    mode === "edit"
      ? `You’re editing “${seedTemplate.name}”.`
      : `You’re working on a copy of “${seedTemplate.name}”.`;
  return `${subject} Describe what you’d like to change and I’ll update the existing layout.`;
};

export const TemplateStudio = (props: TemplateStudioProps) => {
  useScheduledBriefingsFullscreen();
  const flexibleTemplateStudio = useFeature("can-create-templates-exp", false);
  // Stored conversations need the layout-studio session stores (HR-1500).
  const storedConversations =
    useFeature("can-use-template-studio-conversations", false) &&
    flexibleTemplateStudio;
  const navigate = useNavigate();
  const editTemplate = props.mode === "edit" ? props.template : undefined;
  // The template the canvas started from. Clear canvas drops it, so a cleared
  // duplicate behaves like a new template.
  const [seedTemplate, setSeedTemplate] = useState(
    "template" in props ? props.template : undefined,
  );
  const [input, setInput] = useState("");
  const [attachedImages, setAttachedImages] = useState<FileList>();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  // The message held back while the user says where its lone image goes, and that image.
  const [imagePlacementQuestion, setImagePlacementQuestion] = useState<{
    message: string;
    image: { url: string; name: string };
  } | null>(null);
  const [imagePlacementDetails, setImagePlacementDetails] = useState("");
  // The crop or resize message held back while the user says which of several images it means.
  const [imageEditChoice, setImageEditChoice] = useState<{
    message: string;
    images: LayoutImage[];
  } | null>(null);
  const [turns, setTurns] = useState<TemplateStudioTurn[]>([]);
  const [draft, setDraft] = useState<TemplateStudioDraft | null>(
    seedTemplate?.document ?? null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [initialGenerationStageIndex, setInitialGenerationStageIndex] =
    useState(0);
  const [error, setError] = useState<string | null>(null);
  // The layout image open in the crop dialog, the shape it opens with, and whether it can
  // also be resized (body Images), with where it shows on the page.
  const [cropTarget, setCropTarget] = useState<{
    src: string;
    alt: string;
    shape?: TemplateStudioCropShape;
    resize?: boolean;
    fit?: { maxWidth: number; maxHeight?: number };
    /** The original image and the earlier edit, when `src` is a copy the dialog made. */
    source?: CropDialogEdit;
  } | null>(null);
  // Copies the crop dialog made this session, so it reopens on the original, not a copy of a copy.
  const cropDialogEditsRef = useRef(new Map<string, CropDialogEdit>());
  const generateLayoutAbortRef = useRef<AbortController | null>(null);
  // Assets already stored for the composer's images, so a retry uploads only the failed ones.
  const storedAssetsRef = useRef(new Map<File | string, TemplateLayoutAsset>());
  const previewRef = useRef<HTMLDivElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const {
    saveState,
    isSaveModalOpen,
    openSaveModal,
    closeSaveModal,
    submitSave,
    saveModalErrorMessage,
    saveModalInitialMetadata,
    isSavingAsCopy,
    startSaveAsCopy,
    resetSaveState,
  } = useTemplateStudioSave({
    draft,
    initialSavedTemplate: editTemplate,
    initialMetadata:
      props.mode === "duplicate" && seedTemplate != null
        ? {
            name: duplicateTemplateDefaultName(seedTemplate.name),
            description: seedTemplate.description,
          }
        : undefined,
  });

  const conversationScope: TemplateStudioConversationScope =
    props.mode === "edit" || props.mode === "duplicate"
      ? `${props.mode}:${props.template.id}`
      : "create";
  const studioConversation = useStudioConversation({
    enabled: storedConversations,
    scope: conversationScope,
    // Each load of the stored conversation (on open, and after a turn fails
    // or stops) replaces the chat and canvas with what the server has.
    onResume: async (loaded) => {
      const restored = await restoreStudioConversation(loaded);
      setDraft(restored.draft);
      setTurns(restored.turns);
    },
  });
  const {
    conversation,
    isLoading: isConversationLoading,
    error: conversationError,
  } = studioConversation;

  /**
   * After a turn's request fails, the service may still have stored it.
   * Resolves to true when the reloaded conversation shows the turn, so the
   * request error no longer applies.
   */
  const reloadShowsTurn = async (revisionBefore: number) => {
    const stored = await studioConversation.resume();
    return (
      stored != null &&
      (stored.revision > revisionBefore || stored.pending != null)
    );
  };

  // Set once a save succeeds, so the navigation that follows isn't blocked
  // before the clean save state re-renders.
  const hasSavedRef = useRef(false);
  const leaveBlocker = useBlocker({
    shouldBlockFn: () => !hasSavedRef.current,
    disabled: !saveState.isDirty,
    withResolver: true,
  });

  const displaySpec = useMemo(
    () => (draft != null ? getDisplaySpecFromDraft(draft) : null),
    [draft],
  );

  const previewTheme = useMemo(
    () => resolveReportThemeFromDraft(draft),
    [draft],
  );

  // Stored assets and remote links; the dialog copies a link into an asset before cropping.
  // A send that is still running would replace the draft and drop the crop.
  const canCropImage = (src: string) =>
    !isGenerating && /^(https?:\/\/|\/)/i.test(src);

  /** True while the layout still shows `src`, so Undo has something to swap back. */
  const showsLayoutImage = (src: string) =>
    Object.values(draft?.spec.elements ?? {}).some(
      (element) =>
        element.type === "Image" &&
        typeof element.props?.src === "string" &&
        isSameLayoutImage(element.props.src, src),
    );

  const findPreviewImage = (src: string) =>
    Array.from(previewRef.current?.querySelectorAll("img") ?? []).find(
      (image) => isSameLayoutImage(image.getAttribute("src") ?? "", src),
    );

  /** CSS pixels a layout image takes in the preview; undefined when it isn't laid out. */
  const measureShownImageWidth = (src: string) =>
    findPreviewImage(src)?.offsetWidth || undefined;

  /** Opens the crop dialog; a body image also gets its column width and maxHeight in the preview. */
  const openCropDialog = (image: NonNullable<typeof cropTarget>) => {
    const maxWidth = findPreviewImage(image.src)?.closest(
      "figure",
    )?.clientWidth;
    const maxHeight = Object.values(draft?.spec.elements ?? {}).find(
      (element) =>
        element.type === "Image" &&
        typeof element.props?.src === "string" &&
        isSameLayoutImage(element.props.src, image.src),
    )?.props?.maxHeight;
    const source = [...cropDialogEditsRef.current].find(([editedSrc]) =>
      isSameLayoutImage(editedSrc, image.src),
    )?.[1];
    setCropTarget({
      ...image,
      ...(image.resize && maxWidth
        ? {
            fit: {
              maxWidth,
              ...(typeof maxHeight === "number" && maxHeight > 0
                ? { maxHeight }
                : {}),
            },
          }
        : {}),
      ...(source != null ? { source } : {}),
    });
  };

  // Free the attached file's preview once the question closes.
  useEffect(() => {
    const url = imagePlacementQuestion?.image.url;
    return () => {
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    };
  }, [imagePlacementQuestion]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [turns, isGenerating, error]);

  useEffect(() => {
    if (
      !isGenerating ||
      draft != null ||
      initialGenerationStageIndex ===
        TEMPLATE_STUDIO_INITIAL_GENERATION_STAGES.length - 1
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setInitialGenerationStageIndex((current) => current + 1);
    }, TEMPLATE_STUDIO_GENERATION_STAGE_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [draft, initialGenerationStageIndex, isGenerating]);

  const handleStopGeneration = useCallback(() => {
    generateLayoutAbortRef.current?.abort();
    // Free the composer now; a stopped send ignores its uploads when they settle.
    setIsUploadingImages(false);
  }, []);

  const handleSaveTemplate = () => {
    openSaveModal();
  };

  // Without a stored conversation the chat is cleared too, so prompt hints
  // from discarded turns don't return.
  const resetLayoutLocally = (initialDraft: TemplateStudioDraft | null) => {
    setDraft(initialDraft);
    setTurns([]);
    setError(null);
  };

  /**
   * Stores a layout reset to `initialDraft`. The conversation keeps its
   * history, which the service drops from the next brief. Resolves to false
   * if the reset failed.
   */
  const storeLayoutReset = async (
    stored: TemplateStudioConversation,
    initialDraft: TemplateStudioDraft | null,
  ): Promise<boolean> => {
    const command = {
      kind: "reset" as const,
      initialDraft: initialDraft && { ...initialDraft },
      previous: initialDraft
        ? getRefinementPreviousFromDraft(initialDraft)
        : undefined,
      componentTheme: resolveDraftComponentTheme(initialDraft),
    };
    const revisionBefore = stored.revision;
    setIsGenerating(true);
    setError(null);
    try {
      const result = await studioConversation.send(command, { seed: command });
      const turn = result.turns.at(-1);
      setDraft(initialDraft);
      setTurns((previous) => [
        ...previous,
        {
          id: turn?.requestId ?? crypto.randomUUID(),
          text: turn?.text ?? "Reset layout",
          summary: String(turn?.response.summary ?? ""),
        },
      ]);
      return true;
    } catch (failure) {
      // The reload already shows a reset the service stored.
      if (await reloadShowsTurn(revisionBefore)) return true;
      setError(
        failure instanceof Error && failure.message
          ? failure.message
          : "Couldn't reset the layout.",
      );
      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearCanvas = async () => {
    if (conversation == null) resetLayoutLocally(null);
    else if (!(await storeLayoutReset(conversation, null))) return;
    setSeedTemplate(undefined);
    resetSaveState();
  };

  const handleDiscardChanges = () => {
    if (editTemplate == null) return;
    if (conversation == null) resetLayoutLocally(editTemplate.document);
    else void storeLayoutReset(conversation, editTemplate.document);
  };

  /** Keeps the layout and starts the chat over; the next message starts a new stored conversation. */
  const handleNewConversation = () => {
    studioConversation.startNew();
    setTurns([]);
    setError(null);
  };

  const handleAddComponent = (component: string) => {
    setInput((prev) =>
      prev.trim().length === 0
        ? `Add ${component}`
        : `${prev.trim()}, ${component}`,
    );
  };

  /**
   * `placementAnswer` is the user's answer to the image placement question: an option with
   * optional details, their own words, or `null` when they skipped it. `imageEditSrc` is the
   * image the user chose for a crop or resize.
   */
  const handleSubmit = async (
    text?: string,
    placementAnswer?: {
      placement?: TemplateStudioImagePlacement;
      details?: string;
    } | null,
    imageEditSrc?: string,
  ) => {
    const value = (text ?? input).trim();
    if (
      value.length === 0 ||
      isGenerating ||
      isUploadingImages ||
      isConversationLoading
    )
      return;
    // Sending now would start a second conversation; load the stored one first.
    if (conversationError != null) {
      void studioConversation.resume();
      return;
    }

    // Copy pasted image links into assets too, so the template never depends on the remote site.
    const files = Array.from(attachedImages ?? []);
    const pastedImageUrls = findPastedImageUrls(value);
    const sources = [...files, ...pastedImageUrls];
    // With several images, one answer can't say which goes where, so Herald decides.
    const answeredPlacement = placementAnswer?.placement;
    const imagePlacement =
      sources.length === 1
        ? placementAnswer === undefined
          ? resolvePromptImagePlacement(value)
          : (answeredPlacement ?? null)
        : null;
    const [source] = sources;
    if (
      source !== undefined &&
      sources.length === 1 &&
      placementAnswer === undefined &&
      imagePlacement == null
    ) {
      setImagePlacementQuestion({
        message: value,
        image:
          typeof source === "string"
            ? {
                url: source,
                name: new URL(source).pathname.split("/").pop() || "image",
              }
            : { url: URL.createObjectURL(source), name: source.name },
      });
      setImagePlacementDetails("");
      return;
    }
    setImagePlacementQuestion(null);

    // A crop or resize of a layout image runs here, without a model call; attached images go to Herald.
    const imageEditRequest =
      flexibleTemplateStudio && draft != null && sources.length === 0
        ? parseChatImageEditRequest(value)
        : null;
    const layoutImages =
      imageEditRequest != null && draft != null
        ? listLayoutImages(draft.spec)
        : [];
    const chosenImage = layoutImages.find(
      (image) =>
        imageEditSrc != null && isSameLayoutImage(image.src, imageEditSrc),
    );
    const imageEditTarget =
      imageEditRequest == null
        ? null
        : chosenImage != null
          ? { kind: "image" as const, image: chosenImage }
          : resolveChatImageEditTarget(layoutImages, imageEditRequest.target);
    if (imageEditTarget?.kind === "choose") {
      setImageEditChoice({ message: value, images: imageEditTarget.images });
      return;
    }
    setImageEditChoice(null);
    const imageToEdit =
      imageEditTarget?.kind === "image" ? imageEditTarget.image : null;

    const abortController = new AbortController();
    generateLayoutAbortRef.current = abortController;
    const uploadedImages: NonNullable<TemplateStudioTurn["images"]> = [];
    const storedUrlByPastedUrl = new Map<string, string>();
    if (sources.length > 0) {
      setIsUploadingImages(true);
      setImageUploadError(null);
      const uploads = await Promise.all(
        sources.map((source) => {
          const stored = storedAssetsRef.current.get(source);
          if (stored != null) return { success: true as const, data: stored };
          return typeof source === "string"
            ? ingestGraphQLTemplateLayoutAssetFromUrl(source)
            : uploadGraphQLTemplateLayoutAsset(source);
        }),
      );
      uploads.forEach((upload, index) => {
        const source = sources[index];
        if (upload.success && source != null) {
          storedAssetsRef.current.set(source, upload.data);
        }
      });
      // After Stop, a newer send may own the upload state, so leave it alone.
      if (abortController.signal.aborted) return;
      setIsUploadingImages(false);
      const failed = uploads.find((upload) => !upload.success);
      if (failed?.success === false) {
        setImageUploadError(failed.error.message);
        return;
      }
      uploads.forEach((upload, index) => {
        if (!upload.success) return;
        const pastedUrl = pastedImageUrls[index - files.length];
        if (pastedUrl != null) {
          storedUrlByPastedUrl.set(pastedUrl, upload.data.url);
        }
        uploadedImages.push({
          url: upload.data.url,
          name:
            (pastedUrl != null
              ? new URL(pastedUrl).pathname.split("/").pop()
              : files[index]?.name) || "image",
        });
      });
      storedAssetsRef.current.clear();
      setAttachedImages(undefined);
    }

    const request = [
      replacePastedImageUrls(value, storedUrlByPastedUrl),
      answeredPlacement === "top"
        ? "Put the attached image across the top of the report, as the banner."
        : answeredPlacement === "inside"
          ? "Put the attached image inside the report, not as a banner."
          : null,
      placementAnswer?.details
        ? `Where the attached image goes: ${placementAnswer.details}`
        : null,
    ]
      .filter(Boolean)
      .join("\n\n");
    const turnId = crypto.randomUUID();
    const isInitialGeneration = draft === null;
    if (isInitialGeneration) setInitialGenerationStageIndex(0);
    setTurns((prev) => [
      ...prev,
      {
        id: turnId,
        text: value,
        ...(request !== value ? { request } : {}),
        ...(uploadedImages.length > 0 ? { images: uploadedImages } : {}),
      },
    ]);
    setInput("");
    setIsGenerating(true);
    setError(null);

    const imageUrls = uploadedImages.map((image) => image.url);
    const revisionBefore = conversation?.revision ?? 0;

    try {
      // The draft Herald refines: with the edited image in place once the chat has cropped it.
      let baseDraft = draft;
      let handledImageEdit: { src?: string } | undefined;
      if (imageEditRequest != null && imageToEdit != null && draft != null) {
        const outcome = await runChatImageEdit({
          action: imageEditRequest.action,
          image: imageToEdit,
          shownWidth: measureShownImageWidth(imageToEdit.src),
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;
        if (outcome.kind === "failed") {
          setError(outcome.message);
          return;
        }
        if (outcome.kind === "manual") {
          openCropDialog({
            src: imageToEdit.src,
            alt: imageToEdit.alt || (imageToEdit.banner ? "Banner" : "Image"),
            resize: !imageToEdit.banner,
          });
        }
        if (outcome.kind !== "layout") {
          const imageEdit =
            outcome.kind === "edited"
              ? {
                  src: outcome.url,
                  previousSrc: imageToEdit.src,
                  banner: imageToEdit.banner,
                }
              : undefined;
          if (imageEdit != null) {
            baseDraft = {
              ...draft,
              spec: replaceLayoutImage(
                draft.spec,
                imageEdit.previousSrc,
                imageEdit.src,
              ),
            };
            setDraft(baseDraft);
          }
          // Herald stores a stored conversation's turns, so only it can keep an edit on reload.
          const isLocalTurn =
            imageEditRequest.standalone &&
            (imageEdit == null || !storedConversations);
          setTurns((previous) =>
            previous.map((turn) =>
              turn.id === turnId
                ? {
                    ...turn,
                    ...(isLocalTurn
                      ? { summary: outcome.message }
                      : {
                          notes: [
                            ...(turn.notes ?? []),
                            {
                              id: `image-edit:${turnId}`,
                              text: outcome.message,
                            },
                          ],
                        }),
                    ...(imageEdit != null ? { imageEdit } : {}),
                  }
                : imageEdit != null
                  ? withoutSizeTipsFor(turn, imageEdit.previousSrc)
                  : turn,
            ),
          );
          if (isLocalTurn) return;
          handledImageEdit = imageEdit != null ? { src: imageEdit.src } : {};
        }
      }
      const previous = baseDraft
        ? getRefinementPreviousFromDraft(baseDraft)
        : undefined;
      const prompt = buildGenerateLayoutPrompt({
        userPrompt: request,
        googleFont: resolvePromptGoogleFont(value),
        brandColor: resolvePromptBrandColor(value),
        previousSpec: baseDraft?.spec,
        attachedImageUrls: imageUrls,
        handledImageEdit,
      });
      const componentTheme = flexibleTemplateStudio
        ? resolveDraftComponentTheme(baseDraft)
        : undefined;
      // A stored conversation sends Herald the history itself, so the preview
      // context then only describes the canvas.
      const previewContext =
        baseDraft ||
        (flexibleTemplateStudio && !storedConversations && turns.length > 0)
          ? buildTemplateStudioPreviewContext({
              preview: previewRef.current,
              spec: previous ?? null,
              theme: baseDraft?.theme ?? {},
              turns: storedConversations ? undefined : turns,
            })
          : undefined;

      let data: GenerateLayoutApiResponse;
      if (storedConversations) {
        // The conversation runs and stores the turn, and sends Herald the
        // history; `previous` is what the canvas shows now.
        const result = await studioConversation.send(
          {
            kind: "message",
            text: value,
            prompt,
            ...(uploadedImages.length > 0 ? { images: uploadedImages } : {}),
            ...(imagePlacement ? { imagePlacement } : {}),
            previewContext,
            previous,
            componentTheme,
          },
          {
            seed: {
              initialDraft: baseDraft && { ...baseDraft },
              previous,
              componentTheme,
            },
            signal: abortController.signal,
          },
        );
        data = (result.turns.at(-1)?.response ?? {
          ok: false,
        }) as GenerateLayoutApiResponse;
      } else {
        const response = await standardAuthFetch(
          templateStudioGenerateLayoutUrl,
          {
            method: "POST",
            signal: abortController.signal,
            body: JSON.stringify({
              flexibleTemplateStudio,
              prompt,
              previous,
              componentTheme,
              previewContext,
            }),
          },
        );

        if (!response.ok) {
          setError(await parseGenerateLayoutHttpError(response));
          return;
        }

        data = (await response.json()) as GenerateLayoutApiResponse;
      }

      const applied = await applyGenerateLayoutResponse({
        data,
        draft: baseDraft,
        text: value,
        imageUrls,
        imagePlacement,
        flexibleTemplateStudio,
        isFontAvailable: (stylesheetUrl) =>
          isGoogleFontAvailable(stylesheetUrl, abortController.signal),
      });
      if (!applied.ok) {
        setError(applied.error);
        return;
      }
      // Herald keeps the edited copy it was given; this only guards against it reverting.
      setDraft(
        applied.draft != null &&
          imageToEdit != null &&
          handledImageEdit?.src != null
          ? {
              ...applied.draft,
              spec: replaceLayoutImage(
                applied.draft.spec,
                imageToEdit.src,
                handledImageEdit.src,
              ),
            }
          : applied.draft,
      );
      setTurns((previous) =>
        previous.map((turn) =>
          turn.id === turnId
            ? { ...turn, summary: applied.summary, reply: applied.reply }
            : turn,
        ),
      );
      const newBannerSrcs =
        applied.draft != null
          ? findNewBannerSrcs(baseDraft, applied.draft)
          : [];
      // Herald can't see image sizes, so the tip follows in the background and never holds up
      // the preview, the reply or the composer.
      void Promise.all(
        newBannerSrcs.map((src) =>
          loadImageSize(
            resolveLayoutAssetPreviewUrl(src),
            AbortSignal.any([
              abortController.signal,
              AbortSignal.timeout(5_000),
            ]),
          ),
        ),
      ).then((bannerSizes) => {
        // Two banners of the same size give the same text, so key each tip by its src.
        const bannerSizeTips = bannerSizes.flatMap((size, index) => {
          const text = size != null ? resolveBannerSizeTip(size) : null;
          return text != null
            ? [
                {
                  id: `banner-size:${newBannerSrcs[index]}`,
                  text,
                  cropImageSrc: newBannerSrcs[index],
                },
              ]
            : [];
        });
        if (abortController.signal.aborted || bannerSizeTips.length === 0) {
          return;
        }
        setTurns((previous) =>
          previous.map((turn) =>
            turn.id === turnId
              ? { ...turn, notes: [...(turn.notes ?? []), ...bannerSizeTips] }
              : turn,
          ),
        );
      });
    } catch (fetchError) {
      const aborted =
        fetchError instanceof DOMException && fetchError.name === "AbortError";
      // A stopped or failed turn may still finish on the server, so reload
      // the conversation to show what it stored. Stop frees the composer now.
      if (storedConversations) {
        const reload = reloadShowsTurn(revisionBefore);
        if (!aborted && (await reload)) return;
      }
      if (aborted) return;
      if (
        fetchError instanceof Error &&
        fetchError.message === "Session expired"
      ) {
        return;
      }
      setError(
        fetchError instanceof Error
          ? fetchError.message
          : "Layout generation request failed.",
      );
    } finally {
      if (generateLayoutAbortRef.current === abortController) {
        generateLayoutAbortRef.current = null;
      }
      setIsGenerating(false);
    }
  };

  const attachedImagesNoun = attachedImages?.length === 1 ? "image" : "images";

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      <TemplateStudioWorkflowHeader
        isWorking={isGenerating}
        hasCanvasContent={draft != null}
        isSaving={saveState.isSaving}
        onCreateTemplate={handleSaveTemplate}
        {...(editTemplate != null
          ? {
              mode: "edit" as const,
              hasUnsavedChanges: saveState.isDirty,
              onDiscardChanges: handleDiscardChanges,
            }
          : { onClearCanvas: handleClearCanvas })}
        onClose={() => {
          void navigate({ to: "/scheduled-briefings/templates" });
        }}
      />
      <ScheduledBriefingsModals
        saveTemplate={
          draft != null
            ? {
                isOpen: isSaveModalOpen,
                onClose: closeSaveModal,
                onSubmit: (metadata) => {
                  void submitSave(metadata).then((savedTemplate) => {
                    if (savedTemplate == null) return;
                    hasSavedRef.current = true;
                    // The saved template is the starting point from now on.
                    studioConversation.startNew();
                    void navigate({ to: "/scheduled-briefings/templates" });
                  });
                },
                initialMetadata: saveModalInitialMetadata,
                summary: resolveSaveTemplateModalSummary(draft, turns.length),
                isSaving: saveState.isSaving,
                errorMessage: saveModalErrorMessage,
                isUpdate: saveState.savedTemplate != null && !isSavingAsCopy,
                onSaveAsCopy: startSaveAsCopy,
              }
            : undefined
        }
        cropImage={
          cropTarget != null
            ? {
                image: cropTarget.source
                  ? { src: cropTarget.source.src, alt: cropTarget.alt }
                  : cropTarget,
                initialShape: cropTarget.source ? "free" : cropTarget.shape,
                initialEdit: cropTarget.source,
                resize: cropTarget.resize,
                fit: cropTarget.fit,
                onClose: () => setCropTarget(null),
                onApply: (url, edit) => {
                  const originalSrc = cropTarget.source?.src ?? cropTarget.src;
                  cropDialogEditsRef.current.delete(cropTarget.src);
                  if (url !== originalSrc) {
                    cropDialogEditsRef.current.set(url, {
                      src: originalSrc,
                      ...edit,
                    });
                  }
                  setDraft((previous) =>
                    previous != null
                      ? {
                          ...previous,
                          spec: replaceLayoutImage(
                            previous.spec,
                            cropTarget.src,
                            url,
                          ),
                        }
                      : previous,
                  );
                  setTurns((previous) =>
                    previous.map((turn) =>
                      withoutSizeTipsFor(turn, cropTarget.src),
                    ),
                  );
                  setCropTarget(null);
                },
              }
            : undefined
        }
        discardUnsavedChanges={{
          isOpen: leaveBlocker.status === "blocked",
          templateName: editTemplate?.name,
          onLeave: () => leaveBlocker.proceed?.(),
          onStay: () => leaveBlocker.reset?.(),
        }}
      />
      <main className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-white">
        <section
          aria-label="Report layout chat"
          className="flex w-95 shrink-0 flex-col overflow-hidden border-r border-gray-100"
        >
          <TemplateStudioChatHeader isWorking={isGenerating} />
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-300">
            <p className="text-[13px] leading-[21px] text-gray-600">
              {resolveTemplateStudioIntro(props.mode, seedTemplate)}
            </p>
            {conversation != null && !isGenerating ? (
              <button
                type="button"
                onClick={handleNewConversation}
                className="mt-2 cursor-pointer self-start text-xs font-medium text-violet-700 hover:text-violet-800"
              >
                New conversation
              </button>
            ) : null}
            {isConversationLoading ? (
              <p role="status" className="mt-5 text-[13px] text-gray-500">
                Loading your conversation…
              </p>
            ) : null}
            {conversationError != null ? (
              <div
                role="alert"
                className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
              >
                Couldn't load your conversation. {conversationError}{" "}
                <button
                  type="button"
                  onClick={() => void studioConversation.resume()}
                  className="cursor-pointer font-medium underline"
                >
                  Try again
                </button>{" "}
                or{" "}
                <button
                  type="button"
                  onClick={handleNewConversation}
                  className="cursor-pointer font-medium underline"
                >
                  start a new conversation
                </button>
                .
              </div>
            ) : null}
            {!flexibleTemplateStudio && turns.length === 0 && (
              <TemplateStudioEmptyState
                onSelectPrompt={handleSubmit}
                onAddComponent={handleAddComponent}
                showExamplePrompts={seedTemplate == null}
              />
            )}
            {turns.length > 0 && (
              <div className="mt-5 flex flex-col gap-3">
                {turns.map((turn) => (
                  <div key={turn.id} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                      <AttachmentBubbleRow
                        attachments={(turn.images ?? []).map((image) => ({
                          kind: "image",
                          name: image.name,
                          url: resolveLayoutAssetPreviewUrl(image.url),
                        }))}
                      />
                      <UserBubble variant="secondary">{turn.text}</UserBubble>
                    </div>
                    {turn.reply ? (
                      <p className="whitespace-pre-wrap text-[13px] leading-[21px] text-gray-800">
                        {turn.reply}
                      </p>
                    ) : null}
                    {turn.summary ? (
                      <div
                        className={
                          turn.reply
                            ? "text-xs text-gray-500"
                            : "text-[13px] leading-[21px] text-gray-800"
                        }
                      >
                        {turn.summary}
                      </div>
                    ) : null}
                    {turn.notes?.map((note) => (
                      <div
                        key={note.id}
                        className="flex flex-col items-start gap-1.5"
                      >
                        <p className="m-0 text-[13px] leading-[21px] text-gray-800">
                          {note.text}
                        </p>
                        {note.cropImageSrc != null &&
                        flexibleTemplateStudio &&
                        canCropImage(note.cropImageSrc) ? (
                          <button
                            type="button"
                            onClick={() =>
                              openCropDialog({
                                src: note.cropImageSrc ?? "",
                                alt: "Banner",
                                shape: "banner",
                              })
                            }
                            className="rounded-md px-0 text-[13px] font-medium text-violet-700 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-violet-600"
                          >
                            Crop to banner size
                          </button>
                        ) : null}
                      </div>
                    ))}
                    {turn.imageEdit?.undone ? (
                      <p className="m-0 text-xs text-gray-500">
                        Undone: the {turn.imageEdit.banner ? "banner" : "image"}{" "}
                        is back as it was.
                      </p>
                    ) : turn.imageEdit != null &&
                      showsLayoutImage(turn.imageEdit.src) ? (
                      <button
                        type="button"
                        disabled={isGenerating}
                        onClick={() => {
                          const { src, previousSrc } = turn.imageEdit ?? {};
                          if (src == null || previousSrc == null) return;
                          setDraft((previous) =>
                            previous != null
                              ? {
                                  ...previous,
                                  spec: replaceLayoutImage(
                                    previous.spec,
                                    src,
                                    previousSrc,
                                  ),
                                }
                              : previous,
                          );
                          setTurns((previous) =>
                            previous.map((other) =>
                              other.id === turn.id && other.imageEdit != null
                                ? {
                                    ...other,
                                    imageEdit: {
                                      ...other.imageEdit,
                                      undone: true,
                                    },
                                  }
                                : other,
                            ),
                          );
                        }}
                        className="self-start rounded-md px-0 text-[13px] font-normal text-violet-700 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                      >
                        Undo {turn.imageEdit.banner ? "banner" : "image"} edit
                      </button>
                    ) : null}
                  </div>
                ))}
                {isGenerating ? (
                  <div
                    role="status"
                    className="flex items-center gap-2 text-[13px] text-gray-600"
                  >
                    <span aria-hidden className="flex gap-1">
                      {[0, 150, 300].map((delay) => (
                        <span
                          key={delay}
                          className="size-1 rounded-full bg-violet-700 motion-safe:animate-pulse"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </span>
                    {draft === null
                      ? TEMPLATE_STUDIO_INITIAL_GENERATION_STAGES[
                          initialGenerationStageIndex
                        ]
                      : "Thinking through your request"}
                  </div>
                ) : null}
              </div>
            )}
            {error ? (
              <div
                role="alert"
                className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
              >
                {draft != null ? "Your draft is unchanged. " : ""}
                {error}
              </div>
            ) : null}
            <div ref={chatEndRef} />
          </div>
          {isUploadingImages ? (
            <p aria-live="polite" className="px-4 pt-2 text-xs text-gray-500">
              Uploading {attachedImagesNoun}…
            </p>
          ) : imageUploadError != null ? (
            <p role="alert" className="px-4 pt-2 text-xs text-red-600">
              Couldn't upload the {attachedImagesNoun}: {imageUploadError} Try
              sending again.
            </p>
          ) : null}
          {imageEditChoice != null ? (
            <div className="flex flex-col gap-2 border-t border-gray-100 p-3">
              <div className="flex flex-wrap gap-2">
                {imageEditChoice.images.map((image, index) => (
                  <figure
                    key={image.src}
                    className="m-0 flex flex-col items-center gap-1"
                  >
                    <AttachmentBubbleImage
                      src={resolveLayoutAssetPreviewUrl(image.src)}
                      alt={describeLayoutImage(image, index)}
                      className="h-12 w-auto max-w-32 border border-gray-200 bg-white object-contain"
                    />
                    {/* Matches the option's shortcut letter. */}
                    <figcaption
                      aria-hidden
                      className="text-[11px] font-medium text-gray-500"
                    >
                      {String.fromCharCode("A".charCodeAt(0) + index)}
                    </figcaption>
                  </figure>
                ))}
              </div>
              <ChatInputRequestDock
                request={{
                  id: "template-studio-image-edit-target",
                  question: "Which image?",
                  options: imageEditChoice.images.map((image, index) => ({
                    id: image.src,
                    label: describeLayoutImage(image, index),
                  })),
                }}
                showDetails={false}
                onSubmit={(answer) => {
                  if (answer.kind === "choice") {
                    void handleSubmit(
                      imageEditChoice.message,
                      undefined,
                      answer.optionId,
                    );
                  } else {
                    // The message stays in the composer to reword or send.
                    setImageEditChoice(null);
                  }
                }}
              />
            </div>
          ) : imagePlacementQuestion != null ? (
            <div className="flex flex-col gap-2 border-t border-gray-100 p-3">
              <AttachmentBubbleImage
                src={imagePlacementQuestion.image.url}
                alt={imagePlacementQuestion.image.name}
                // The whole image, so a wide logo isn't cropped to a square.
                className="h-12 w-auto max-w-48 self-start border border-gray-200 bg-white object-contain"
              />
              <ChatInputRequestDock
                request={{
                  id: "template-studio-image-placement",
                  question: "Where should this image go?",
                  options: [
                    { id: "top", label: "Across the top of the report" },
                    { id: "inside", label: "Inside the report" },
                  ],
                }}
                details={imagePlacementDetails}
                onDetailsChange={setImagePlacementDetails}
                onSubmit={(answer) => {
                  const details =
                    answer.kind === "choice"
                      ? answer.details?.trim()
                      : answer.kind === "other"
                        ? answer.text
                        : undefined;
                  void handleSubmit(
                    imagePlacementQuestion.message,
                    answer.kind === "skip"
                      ? null
                      : {
                          ...(answer.kind === "choice"
                            ? {
                                placement:
                                  answer.optionId === "top" ? "top" : "inside",
                              }
                            : {}),
                          ...(details ? { details } : {}),
                        },
                  );
                }}
              />
            </div>
          ) : (
            <ChatInput
              value={input}
              onChange={setInput}
              onSubmit={handleSubmit}
              onStop={handleStopGeneration}
              isStreaming={isGenerating || isUploadingImages}
              variant="studio"
              files={attachedImages}
              onFilesChange={(files) => {
                setAttachedImages(files);
                setImageUploadError(null);
              }}
              imagesOnly
              maxLength={flexibleTemplateStudio ? 128_000 : undefined}
              footerNote={
                TEMPLATE_STUDIO_CHAT_FOOTER_NOTE[
                  props.mode === "edit" ? "edit" : "create"
                ]
              }
            />
          )}
        </section>
        <section
          aria-label="Report layout preview"
          className="flex min-w-0 flex-1 flex-col overflow-hidden bg-gray-50"
        >
          <div className="relative min-h-0 flex-1 overflow-auto p-6 pt-5">
            <div
              className="mx-auto w-full"
              style={{ maxWidth: A4_PREVIEW_WIDTH_PX }}
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                Preview · Sample content
              </p>
              <div className="relative pt-3" ref={previewRef}>
                {displaySpec ? (
                  <ReportImageCropContext.Provider
                    value={
                      flexibleTemplateStudio
                        ? { canCrop: canCropImage, onCrop: openCropDialog }
                        : null
                    }
                  >
                    <ContinuousA4Preview
                      spec={displaySpec}
                      theme={previewTheme}
                    />
                  </ReportImageCropContext.Provider>
                ) : isGenerating ? (
                  <TemplateStudioCanvasSkeleton />
                ) : (
                  <TemplateStudioEmptyCanvas />
                )}
                {isGenerating && draft ? (
                  <div
                    aria-live="polite"
                    className="pointer-events-none absolute right-4 top-4"
                  >
                    <p className="rounded-full bg-white px-4 py-2 text-sm text-gray-500 shadow-sm">
                      Herald is thinking…
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
